// SORRY BOSS — Auth Screen (BETA 22.52)
// The first thing a fresh boot shows after the tear — so it speaks the same
// language as the menu: monster-army backdrop, gunmetal plate, corner screws,
// grunge film, Anton + Share Tech Mono, ember/purple palette. The classroom
// photo + "BURNT PEANUT SIMULATOR" era is gone (Dylan: "all that old UI needs
// to be removed"). ALL auth logic (Supabase sign-in, waitlist, tester codes)
// is unchanged — this is a reskin of the shell only.

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { signIn, joinWaitlist } from '../lib/supabase';
import { validateDevCode } from '../game/devUsers';
import { resolveAsset } from '../game/uiAssets';

interface AuthScreenProps {
  onSuccess: (username: string) => void;
  onSkip?: () => void;
  /** BETA 21.06 — a valid dev access code logs the tester straight in (no
   *  email/password/confirmation). Receives the resolved dev username. */
  onDevCode?: (username: string) => void;
  /** BETA 22.52 — rendered as an overlay ON TOP of the live menu (raised by
   *  START RUN while signed out) instead of a full-screen boot wall: dark
   *  scrim instead of its own background, closable with ✕ / Esc. */
  overlay?: boolean;
  onClose?: () => void;
}

const MONO = "'Share Tech Mono', 'Courier New', monospace";
const HEAVY = "'Anton', 'Archivo Black', 'Arial Black', sans-serif";
const NOISE =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")';

const C = {
  white:  '#dde4ee',
  bright: '#f2f4f8',
  dim:    '#8b93a1',
  ember:  '#ff8c3a',
  purple: '#b44dff',
  gold:   '#e8b64f',
  error:  '#ff6a5e',
};

export default function AuthScreen({ onSuccess, onSkip, onDevCode, overlay, onClose }: AuthScreenProps) {
  // Overlay flavor is deliberately COMPACT (Dylan: "sign in box is huge") —
  // it's a quick speed bump over the menu, not a landing page.
  const compact = !!overlay;
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [peanutName, setPeanutName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [showDevCode, setShowDevCode] = useState(false);

  // BETA 21.06 — tester access code: a valid code logs straight in as a dev,
  // no email/password/confirmation. Validated locally against DEV_ACCESS_CODES.
  const handleDevCode = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const name = validateDevCode(devCode);
    if (name && onDevCode) {
      onDevCode(name);
    } else {
      setError('That tester code isn\'t valid.');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(5,7,11,0.7)',
    border: '1px solid rgba(150,170,195,0.25)',
    borderRadius: 4,
    padding: compact ? '8px 10px' : '10px 12px',
    color: C.bright,
    fontFamily: MONO,
    fontSize: compact ? '0.78rem' : '0.88rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    color: C.dim, fontSize: '0.7rem', letterSpacing: '0.15em', marginBottom: 5, fontFamily: MONO,
  };

  const switchMode = (m: 'signin' | 'signup') => {
    setMode(m);
    setError('');
    setWaitlistDone(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await signIn(email.trim(), password);
      const user = data.user;
      if (!user) throw new Error('Login failed.');
      const { supabase } = await import('../lib/supabase');
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      onSuccess(profile?.username ?? email.split('@')[0]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    setError('');
    setLoading(true);
    try {
      await joinWaitlist(email.trim(), peanutName.trim() || undefined);
      setWaitlistDone(true);
      setEmail('');
      setPeanutName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // Esc closes the overlay flavor (never the boot-wall flavor).
  useEffect(() => {
    if (!overlay || !onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlay, onClose]);

  // BETA 22.74 — register a focus screen while the overlay is up. The sign-in
  // renders ON TOP of a still-mounted StartMenu (Home keeps it mounted); the
  // new StartMenu gamepad poller sleeps whenever getActiveScreen() is set, so
  // without this a controller kept cycling survivors BEHIND the sign-in.
  // Registering also gives the overlay the shared DOM focus-cycling (B closes
  // via onBack; typing still needs keyboard/touch — flagged in the changelog).
  useEffect(() => {
    if (!overlay || !onClose) return;
    let cancelled = false;
    const setup = async () => {
      const focusMod = await import('../game/gamepadFocus');
      if (cancelled) return;
      focusMod.registerFocusScreen('auth_overlay', [], () => onClose(), 0);
      focusMod.setActiveScreen('auth_overlay');
    };
    setup();
    return () => {
      cancelled = true;
      import('../game/gamepadFocus').then(focusMod => {
        focusMod.unregisterFocusScreen('auth_overlay');
        if (focusMod.getActiveScreen() === 'auth_overlay') focusMod.setActiveScreen(null);
      });
    };
  }, [overlay, onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={overlay ? undefined : {
        backgroundImage: `url(${resolveAsset('/menu/menu_bg_main.jpg')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* the war outside stays dark + far away while you sign in */}
      <div style={{ position: 'absolute', inset: 0, background: overlay ? 'rgba(4,3,9,0.7)' : 'rgba(4,3,9,0.78)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, transparent 30%, rgba(4,3,9,0.55) 100%)' }} />

      <motion.div
        style={{ width: '100%', maxWidth: compact ? 360 : 440, padding: '0 16px', position: 'relative', zIndex: 1 }}
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        {/* SORRY BOSS wordmark above the plate — boot-wall flavor only; the
            overlay floats on the menu, which already wears the corner sticker. */}
        {!overlay && (
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <img src={resolveAsset('/menu/sorry_boss_logo.png')} alt="Sorry Boss"
              style={{ width: 300, maxWidth: '70%', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.7))' }} />
          </div>
        )}

        <div
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 20% 110%, rgba(0,0,0,0.5), transparent 60%),' +
              'radial-gradient(ellipse 50% 40% at 85% -10%, rgba(120,135,155,0.12), transparent 60%),' +
              'linear-gradient(180deg, #262b33 0%, #191d24 45%, #0f1218 100%)',
            border: '2px solid #05070b',
            outline: '1px solid rgba(150,170,195,0.16)',
            outlineOffset: -5,
            borderRadius: 10,
            boxShadow: 'inset 0 2px 0 rgba(200,215,235,0.10), inset 0 -3px 6px rgba(0,0,0,0.55), 0 18px 50px rgba(0,0,0,0.75)',
            fontFamily: MONO,
            padding: compact ? '18px 22px 15px' : '26px 28px 22px',
            position: 'relative',
            overflow: 'hidden',
            maxHeight: '86vh',
            overflowY: 'auto',
          }}
        >
          {/* ✕ — only when raised as an overlay (browsing must stay free) */}
          {overlay && onClose && (
            <button onClick={onClose} aria-label="close sign in"
              style={{ position: 'absolute', right: 10, top: 8, width: 34, height: 34, zIndex: 2,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: C.dim, fontFamily: MONO, fontSize: '1.05rem' }}>✕</button>
          )}

          {/* grunge film + corner screws — same plate language as the menu */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: NOISE, opacity: 0.1, mixBlendMode: 'overlay', pointerEvents: 'none' }} />
          {([{ left: 10, top: 10 }, { right: 10, top: 10 }, { left: 10, bottom: 10 }, { right: 10, bottom: 10 }] as React.CSSProperties[]).map((pos, i) => (
            <span key={i} style={{ position: 'absolute', ...pos,
              width: 7, height: 7, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #9aa5b5, #2b313b 70%)', boxShadow: '0 1px 1px rgba(0,0,0,0.8)' }} />
          ))}

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: compact ? 12 : 20, position: 'relative' }}>
            <div style={{ fontFamily: HEAVY, color: C.white, fontSize: compact ? '0.98rem' : '1.15rem', letterSpacing: '0.12em' }}>
              CLOCK IN
            </div>
            <div style={{ color: C.dim, fontSize: '0.7rem', letterSpacing: '0.2em', marginTop: 3 }}>
              {mode === 'signin' ? 'SIGN IN TO YOUR ACCOUNT' : '🤫 CLASSIFIED'}
            </div>
          </div>

          {/* Tab toggle */}
          <div style={{ display: 'flex', marginBottom: compact ? 14 : 20, borderBottom: '1px solid rgba(150,170,195,0.2)', position: 'relative' }}>
            {(['signin', 'signup'] as const).map(m => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: mode === m ? `2px solid ${C.ember}` : '2px solid transparent',
                  color: mode === m ? C.ember : C.dim,
                  fontFamily: MONO,
                  fontSize: '0.75rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {m === 'signin' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* ── SIGN IN FORM ── */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: compact ? 11 : 14, position: 'relative' }}>
              <div>
                <div style={labelStyle}>EMAIL</div>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div>
                <div style={labelStyle}>PASSWORD</div>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <div style={{ color: C.error, fontSize: '0.75rem', letterSpacing: '0.05em', padding: '8px 10px', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 4 }}>
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: compact ? '10px 0' : '12px 0',
                  background: loading
                    ? 'rgba(120,40,20,0.3)'
                    : 'linear-gradient(180deg, #d43527 0%, #b01e15 55%, #7c100c 100%)',
                  border: '2px solid #16100d',
                  borderRadius: 6,
                  color: loading ? C.dim : '#ffe9e2',
                  fontFamily: HEAVY,
                  fontSize: compact ? '0.85rem' : '0.95rem',
                  letterSpacing: '0.16em',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.18), inset 0 -6px 12px rgba(0,0,0,0.4)',
                  transition: 'all 0.12s',
                }}
              >
                {loading ? 'LOADING...' : 'SIGN IN'}
              </button>
            </form>
          )}

          {/* ── TESTER ACCESS CODE (BETA 21.06) — signin tab only ── */}
          {mode === 'signin' && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(150,170,195,0.14)', position: 'relative' }}>
              {!showDevCode ? (
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => { setShowDevCode(true); setError(''); }}
                    style={{ background: 'transparent', border: 'none', color: C.dim, fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.12em', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
                  >
                    have a tester code?
                  </button>
                </div>
              ) : (
                <form onSubmit={handleDevCode} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={labelStyle}>TESTER ACCESS CODE</div>
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="your access code"
                    value={devCode}
                    onChange={e => setDevCode(e.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                  <button
                    type="submit"
                    style={{
                      padding: '10px 0',
                      background: 'rgba(180,77,255,0.10)',
                      border: '1px solid rgba(180,77,255,0.45)',
                      borderRadius: 4,
                      color: C.purple,
                      fontFamily: MONO,
                      fontSize: '0.8rem',
                      letterSpacing: '0.2em',
                      cursor: 'pointer',
                    }}
                  >
                    ENTER AS TESTER
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── WAITLIST FORM ── */}
          {mode === 'signup' && !waitlistDone && (
            <form onSubmit={handleWaitlist} style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
              <div style={{ textAlign: 'center', marginBottom: 2 }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🤫</div>
                <div style={{ color: C.gold, fontSize: '0.8rem', letterSpacing: '0.14em', marginBottom: 8 }}>
                  DON'T TELL THE BOSS
                </div>
                <div style={{ color: C.dim, fontSize: '0.68rem', letterSpacing: '0.06em', lineHeight: 1.7 }}>
                  drop your email below. when the game officially<br />
                  launches, you'll be the first to know.
                </div>
              </div>

              <div>
                <div style={labelStyle}>
                  YOUR CALLSIGN <span style={{ opacity: 0.5 }}>(optional)</span>
                </div>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="xXBagHeadXx"
                  value={peanutName}
                  onChange={e => setPeanutName(e.target.value)}
                  maxLength={20}
                  autoComplete="off"
                />
              </div>

              <div>
                <div style={labelStyle}>EMAIL</div>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>

              {error && (
                <div style={{ color: C.error, fontSize: '0.75rem', letterSpacing: '0.05em', padding: '8px 10px', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 4 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: '11px 0',
                  background: loading ? 'rgba(232,182,79,0.05)' : 'rgba(232,182,79,0.12)',
                  border: `1px solid ${loading ? 'rgba(232,182,79,0.2)' : 'rgba(232,182,79,0.5)'}`,
                  borderRadius: 4,
                  color: loading ? C.dim : C.gold,
                  fontFamily: MONO,
                  fontSize: '0.8rem',
                  letterSpacing: '0.18em',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {loading ? 'SHHHH...' : "I'M IN. KEEP IT QUIET."}
              </button>
            </form>
          )}

          {/* ── WAITLIST SUCCESS ── */}
          {mode === 'signup' && waitlistDone && (
            <div style={{ textAlign: 'center', padding: '8px 0 4px', position: 'relative' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: 14 }}>💀</div>
              <div style={{ color: C.gold, fontSize: '0.85rem', letterSpacing: '0.18em', marginBottom: 12 }}>
                YOU'RE ON THE LIST.
              </div>
              <div style={{ color: C.dim, fontSize: '0.7rem', letterSpacing: '0.07em', lineHeight: 1.8 }}>
                We'll send you a signal when the time comes.<br />
                Don't breathe a word to the boss.
              </div>
              <div style={{ marginTop: 22, color: 'rgba(139,147,161,0.5)', fontSize: '0.62rem', letterSpacing: '0.1em' }}>
                — already a tester? —
              </div>
              <button
                onClick={() => switchMode('signin')}
                style={{
                  marginTop: 8,
                  background: 'transparent',
                  border: 'none',
                  color: C.dim,
                  fontFamily: MONO,
                  fontSize: '0.68rem',
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                sign in here
              </button>
            </div>
          )}

          {/* Skip */}
          {onSkip && (
            <div style={{ textAlign: 'center', marginTop: 16, position: 'relative' }}>
              <button
                onClick={onSkip}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: C.dim,
                  fontFamily: MONO,
                  fontSize: '0.68rem',
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                play as guest (no leaderboard)
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
